/**
 * DeviceRow — single row in the ActiveSessionsPanel device list.
 *
 * Extracted from ActiveSessionsPanel.jsx in v167.x to keep the parent
 * component small (≤150 lines) and lower cyclomatic complexity. Pure
 * presentational + callback-driven: state lives in the parent.
 */
import React from 'react';
import {
  Monitor, MapPin, Clock, ShieldOff, Pencil, X, Check,
  Laptop, Smartphone, Globe, ChevronRight,
} from 'lucide-react';

export function deviceIcon(label = '', ua = '') {
  const s = `${label} ${ua}`.toLowerCase();
  if (/iphone|ipad|android|mobile|phone/.test(s)) return Smartphone;
  if (/mac|windows|linux|laptop|chrome|edge|firefox|safari/.test(s)) return Laptop;
  return Globe;
}

export function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString();
}

// ── Sub-pieces — kept inside this file so the row composition stays
// in one place; promoting to top-level files is overkill at this size.

function DeviceIconBubble({ revoked, isCurrent, Icon }) {
  const palette = revoked
    ? { bg: 'rgba(148,163,184,0.10)', fg: '#94A3B8', border: 'rgba(148,163,184,0.30)' }
    : isCurrent
      ? { bg: 'rgba(16,185,129,0.10)', fg: '#10B981', border: 'rgba(16,185,129,0.40)' }
      : { bg: 'rgba(174,82,234,0.10)', fg: '#C084FC', border: 'rgba(174,82,234,0.40)' };
  return (
    <div
      className="h-9 w-9 rounded flex items-center justify-center shrink-0 border"
      style={{ backgroundColor: palette.bg, color: palette.fg, borderColor: palette.border }}
    >
      <Icon size={15} />
    </div>
  );
}

function DeviceLabel({ device, isEditing, editValue, onEditChange, isDark }) {
  if (isEditing) {
    return (
      <input
        value={editValue}
        onChange={(e) => onEditChange(e.target.value)}
        data-testid={`device-rename-input-${device.device_id}`}
        className="text-sm font-semibold bg-transparent border-b border-violet-400 focus:outline-none px-1 py-0.5 max-w-[14rem]"
        autoFocus
        maxLength={60}
      />
    );
  }
  return (
    <span
      className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
      data-testid={`device-label-${device.device_id}`}
    >
      {device.label || 'Unnamed device'}
    </span>
  );
}

function DeviceMeta({ device }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-white/55 font-mono">
      {device.last_seen_geo?.code && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border bg-[rgba(174,82,234,0.08)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.30)] text-[10px]"
          data-testid={`device-geo-${device.device_id}`}
          title={device.last_seen_geo.name}
        >
          <span className="text-sm leading-none">{device.last_seen_geo.flag}</span>
          <span className="font-semibold tracking-wide">{device.last_seen_geo.name}</span>
        </span>
      )}
      {device.last_seen_ip && (
        <span className="inline-flex items-center gap-1">
          <MapPin size={9} className="opacity-70" />
          {device.last_seen_ip}
        </span>
      )}
      {device.last_seen_ua && (
        <span className="inline-flex items-center gap-1 truncate max-w-full">
          <Monitor size={9} className="opacity-70" />
          {device.last_seen_ua.slice(0, 80)}{device.last_seen_ua.length > 80 ? '…' : ''}
        </span>
      )}
      <span className="inline-flex items-center gap-1 opacity-70">
        <ChevronRight size={9} />
        first seen {relativeTime(device.first_seen_at)}
      </span>
    </div>
  );
}

function DeviceActions({
  device, revoked, isEditing, isPending,
  onStartEdit, onCancelEdit, onSave, onRevoke,
}) {
  if (revoked) return null;
  if (isEditing) {
    return (
      <>
        <button
          onClick={() => onSave(device)}
          disabled={isPending}
          data-testid={`device-rename-save-${device.device_id}`}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 disabled:opacity-50 inline-flex items-center gap-1"
        >
          <Check size={10}/> Save
        </button>
        <button
          onClick={onCancelEdit}
          data-testid={`device-rename-cancel-${device.device_id}`}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1"
        >
          <X size={10}/> Cancel
        </button>
      </>
    );
  }
  return (
    <>
      <button
        onClick={() => onStartEdit(device)}
        data-testid={`device-rename-${device.device_id}`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1"
      >
        <Pencil size={10}/> Rename
      </button>
      <button
        onClick={() => onRevoke(device)}
        disabled={isPending}
        data-testid={`device-revoke-${device.device_id}`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border bg-rose-500/10 text-rose-500 border-rose-500/40 hover:bg-rose-500/20 disabled:opacity-50 inline-flex items-center gap-1"
      >
        <ShieldOff size={10}/>
        {isPending ? 'Revoking…' : device.is_current ? 'Sign out this device' : 'Revoke this device'}
      </button>
    </>
  );
}

export default function DeviceRow({
  device, isDark, isEditing, editValue, isPending,
  onEditChange, onStartEdit, onCancelEdit, onSave, onRevoke,
}) {
  const Icon = deviceIcon(device.label, device.last_seen_ua);
  const revoked = device.is_revoked;
  const containerTone = revoked
    ? (isDark ? 'border-white/5 bg-white/[0.01] opacity-60' : 'border-slate-200 bg-slate-50 opacity-60')
    : device.is_current
      ? (isDark ? 'border-emerald-500/30 bg-emerald-500/[0.04]' : 'border-emerald-300 bg-emerald-50/40')
      : (isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-200 bg-slate-50');

  return (
    <li
      data-testid={`device-row-${device.device_id}`}
      className={`flex items-start gap-3 px-3 py-3 rounded-md border ${containerTone}`}
    >
      <DeviceIconBubble revoked={revoked} isCurrent={device.is_current} Icon={Icon} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <DeviceLabel
              device={device}
              isEditing={isEditing}
              editValue={editValue}
              onEditChange={onEditChange}
              isDark={isDark}
            />
            {device.is_current && !revoked && (
              <span
                data-testid={`device-current-${device.device_id}`}
                className="px-1.5 py-0.5 rounded-sm border bg-emerald-500/10 text-emerald-500 border-emerald-500/40 text-[9px] tracking-[0.18em] uppercase font-bold"
              >
                This device
              </span>
            )}
            {revoked && (
              <span
                data-testid={`device-revoked-${device.device_id}`}
                className="px-1.5 py-0.5 rounded-sm border bg-rose-500/10 text-rose-500 border-rose-500/40 text-[9px] tracking-[0.18em] uppercase font-bold"
              >
                Revoked
              </span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/45 font-mono inline-flex items-center gap-1">
            <Clock size={9} className="opacity-70" />
            {relativeTime(device.last_seen_at)}
          </span>
        </div>
        <DeviceMeta device={device} />
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <DeviceActions
            device={device}
            revoked={revoked}
            isEditing={isEditing}
            isPending={isPending}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onSave={onSave}
            onRevoke={onRevoke}
          />
        </div>
      </div>
    </li>
  );
}
